import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Maximize2Icon,
  XIcon,
} from "lucide-react";

import { StafferQuoMessages } from "./StafferQuoMessages";
import { Center, Column, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { formatting } from "@/lib/formatting";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

export const CandidacyMessagesSheet = ({
  candidacyId,
  canMoveToNextCandidacy,
  canMoveToPreviousCandidacy,
  currentCandidacyIndex,
  handleMoveToNextCandidacy,
  handleMoveToPreviousCandidacy,
  onClose,
  onOpenProfile,
  totalCandidaciesCount,
}: {
  candidacyId: string | null;
  canMoveToNextCandidacy: boolean;
  canMoveToPreviousCandidacy: boolean;
  currentCandidacyIndex: number;
  handleMoveToNextCandidacy: () => void;
  handleMoveToPreviousCandidacy: () => void;
  onClose: () => void;
  onOpenProfile: () => void;
  totalCandidaciesCount: number;
}) => {
  /* Render */
  const render = () => (
    <Sheet
      open={candidacyId !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        className="flex w-[475px] flex-col gap-0 p-0 sm:max-w-[560px]"
        hideClose
        stopWheelPropagation
      >
        <SheetTitle className="sr-only">Candidate messages</SheetTitle>
        <SheetDescription className="sr-only">
          Send and review Quo messages with this candidate.
        </SheetDescription>
        {candidacyId !== null && (
          <CandidacyMessagesSheetBody
            candidacyId={candidacyId}
            canMoveToNextCandidacy={canMoveToNextCandidacy}
            canMoveToPreviousCandidacy={canMoveToPreviousCandidacy}
            currentCandidacyIndex={currentCandidacyIndex}
            handleMoveToNextCandidacy={handleMoveToNextCandidacy}
            handleMoveToPreviousCandidacy={handleMoveToPreviousCandidacy}
            onClose={onClose}
            onOpenProfile={onOpenProfile}
            totalCandidaciesCount={totalCandidaciesCount}
          />
        )}
      </SheetContent>
    </Sheet>
  );

  return render();
};

const CandidacyMessagesSheetBody = ({
  candidacyId,
  canMoveToNextCandidacy,
  canMoveToPreviousCandidacy,
  currentCandidacyIndex,
  handleMoveToNextCandidacy,
  handleMoveToPreviousCandidacy,
  onClose,
  onOpenProfile,
  totalCandidaciesCount,
}: {
  candidacyId: string;
  canMoveToNextCandidacy: boolean;
  canMoveToPreviousCandidacy: boolean;
  currentCandidacyIndex: number;
  handleMoveToNextCandidacy: () => void;
  handleMoveToPreviousCandidacy: () => void;
  onClose: () => void;
  onOpenProfile: () => void;
  totalCandidaciesCount: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo, isLoading } = useGetCandidacyInfo({
    candidacyId,
  });
  const workerId = candidacyInfo?.talentId ?? 0;
  const profileQuery = useGetCandidacySheetData({
    jobId: candidacyInfo?.jobId,
    userId: workerId,
  });
  const profileData = profileQuery.data;
  // Hold the loader until the profile lands too — the composer needs the
  // phone and the header needs the name. Errors fall through to fallbacks
  // rather than spinning forever.
  const isProfileLoading =
    workerId > 0 && profileQuery.isPending && !profileQuery.isError;

  /* Handlers */
  const handleOpenProfile = () => {
    mixpanelUtils.trackButtonClick(
      "Open profile from messages sheet",
      MIXPANEL_SCREENS.JOB,
    );
    onOpenProfile();
  };

  const handlePrevious = () => {
    mixpanelUtils.trackButtonClick(
      "Previous candidate from messages sheet",
      MIXPANEL_SCREENS.JOB,
    );
    handleMoveToPreviousCandidacy();
  };

  const handleNext = () => {
    mixpanelUtils.trackButtonClick(
      "Next candidate from messages sheet",
      MIXPANEL_SCREENS.JOB,
    );
    handleMoveToNextCandidacy();
  };

  /* Render */
  const render = () => {
    if (isLoading || !candidacyInfo || isProfileLoading) {
      return (
        <Center className="min-h-0 flex-1">
          <LoadingIndicator variant="large" />
        </Center>
      );
    }

    const profile = profileData?.profile;
    const fullName = formatting.fullName(
      profile?.firstName,
      profile?.lastName,
      "Candidate",
    );

    return (
      <Column className="min-h-0 flex-1">
        <Row className="relative items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2">
          <Row className="min-w-0 items-center gap-2">
            <Button
              aria-label="Close messages"
              className="h-8 w-8 text-slate-500"
              onClick={onClose}
              size="icon"
              variant="ghost"
            >
              <XIcon className="h-4 w-4" />
            </Button>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Row className="min-w-0 max-w-[130px] items-center rounded-full border border-slate-200 px-2.5 py-0.5">
                    <Text className="truncate text-xs font-medium text-slate-700">
                      {fullName}
                    </Text>
                  </Row>
                </TooltipTrigger>
                <TooltipContent>{fullName}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Row>
          <Button
            className="absolute left-1/2 ml-[10px] h-7 -translate-x-1/2 gap-1 px-2.5 text-xs font-medium text-slate-700"
            onClick={handleOpenProfile}
            size="sm"
            variant="outline"
          >
            <Maximize2Icon className="h-3.5 w-3.5" />
            Open profile
          </Button>
          <Row className="items-center justify-end gap-0.5">
            <Button
              aria-label="Previous candidate"
              className="h-8 w-8 text-slate-500"
              disabled={!canMoveToPreviousCandidacy}
              onClick={handlePrevious}
              size="icon"
              variant="ghost"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            {currentCandidacyIndex >= 0 ? (
              <Text className="text-xs font-medium tabular-nums text-slate-500">
                {currentCandidacyIndex + 1} of{" "}
                {totalCandidaciesCount.toLocaleString()}
              </Text>
            ) : null}
            <Button
              aria-label="Next candidate"
              className="h-8 w-8 text-slate-500"
              disabled={!canMoveToNextCandidacy}
              onClick={handleNext}
              size="icon"
              variant="ghost"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </Row>
        </Row>
        <Column className="min-h-0 flex-1 p-4">
          <StafferQuoMessages
            candidacy={{
              firstName: profile?.firstName ?? null,
              id: candidacyId,
              lastName: profile?.lastName ?? null,
              phone: candidacyInfo.phone ?? profile?.phone ?? null,
              stafferAgentActive: candidacyInfo.stafferAgentActive,
              status: candidacyInfo.status,
              talentId: candidacyInfo.talentId ?? null,
            }}
            phone={candidacyInfo.phone ?? profile?.phone ?? null}
            talentId={candidacyInfo.talentId ?? null}
          />
        </Column>
      </Column>
    );
  };

  return render();
};
